import { Outlet, createRootRoute, createRoute } from '@tanstack/react-router';
import { useEffect, type ReactNode } from 'react';
import { formatDateKey } from '../scheduleView';
import type { ScheduleMode, WorkspaceTab } from '../workspaceRoutes';
import {
  AssignmentCreatePage,
  AssignmentDetailPage,
  AssignmentSubmissionPage,
  WorkspaceAssignmentsTab
} from './assignments';
import { AuthPage, OnboardingPage } from './authOnboarding';
import type { WorkspaceBootstrapContext } from './appTypes';
import { useWorkspaceBootstrapContext, useWorkspaceRouteAdapter } from './context';
import {
  ApprovalDetailPage,
  ApprovalListPage,
  ChoirManagementPage,
  CreateAttendanceCategoryPage,
  DisableAttendanceCategoryPage,
  InvitationCodesPage,
  RenameAttendanceCategoryPage,
  RoutePlaceholder,
  ScheduleSettingsPage
} from './management';
import {
  CreateMemberGroupInitialMemberPage,
  CreateMemberGroupPage,
  CreateMemberGroupTagPage,
  DeleteMemberGroupPage,
  DeleteMemberGroupTagPage,
  ManageMemberGroupMembersPage,
  ManageMemberGroupTagsPage,
  MemberGroupDetailPage,
  MemberGroupTagDetailPage,
  MemberGroupsPage,
  MemberSettingsPage,
  RemoveMemberGroupMemberPage,
  RenameMemberGroupPage,
  RenameMemberGroupTagPage,
  isMemberGroupDetailTab,
  type MemberGroupDetailTab
} from './memberGroups';
import { isWorkspaceTab } from './navigation';
import { NotificationsPage } from './notifications';
import {
  CreatePermissionGroupPage,
  DeletePermissionGroupPage,
  EditPermissionGroupPage,
  ManagePermissionGroupMembersPage,
  PermissionGroupDetailPage,
  PermissionGroupsPage,
  RemovePermissionGroupMemberPage
} from './permissionGroups';
import { ResourceFilePreviewPage, SongDetailPage, SongRelatedFolderPage } from './resources';
import {
  ScheduleCreateAttendancePage,
  ScheduleCreatePage,
  ScheduleCreateTimePage,
  ScheduleDeletePage,
  ScheduleDetailPage,
  ScheduleEditAttendancePage,
  ScheduleEditPage,
  ScheduleEditTimePage,
  ScheduleRows,
  WorkspaceScheduleTab
} from './schedule';
import { AppShell } from './shell';
import { WorkspaceHomeTab, WorkspaceMembersTab, WorkspaceResourcesTab } from './workspaceTabs';

type WorkspaceTabSearch = {
  readonly mode?: ScheduleMode;
  readonly date?: string;
  readonly month?: string;
};
type ScheduleCreateSearch = {
  readonly date?: string;
};
type MemberGroupDetailSearch = {
  readonly tab?: MemberGroupDetailTab;
};

const DATE_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const MONTH_KEY_PATTERN = /^\d{4}-\d{2}$/;

const rootRoute = createRootRoute({
  component: AppShellRoute
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: IndexRoute
});

const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'auth',
  component: AuthRoute
});

const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'onboarding',
  component: OnboardingRoute
});

const workspaceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'workspace',
  component: WorkspaceRouteOutlet
});

const workspaceTabRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: '$tab',
  validateSearch: validateWorkspaceTabSearch,
  component: WorkspaceTabRoute
});

const notificationsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'notifications',
  component: NotificationsRoute
});

const songDetailRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'resources/songs/$songId',
  component: SongDetailRoute
});

const songRelatedFolderRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'resources/songs/$songId/folders/$folderId',
  component: SongRelatedFolderRoute
});

const resourceFilePreviewRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'resources/files/$resourceFileId',
  component: ResourceFilePreviewRoute
});

const scheduleDetailRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'schedules/$scheduleId',
  component: ScheduleDetailRoute
});

const scheduleCreateRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'schedules/new',
  validateSearch: validateScheduleCreateSearch,
  component: ScheduleCreateRoute
});

const scheduleEditRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'schedules/$scheduleId/edit',
  component: ScheduleEditRoute
});

const scheduleCreateTimeRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'schedules/new/time',
  component: ScheduleCreateTimeRoute
});

const scheduleEditTimeRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'schedules/$scheduleId/edit/time',
  component: ScheduleEditTimeRoute
});

const scheduleCreateAttendanceRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'schedules/new/attendance',
  component: ScheduleCreateAttendanceRoute
});

const scheduleEditAttendanceRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'schedules/$scheduleId/edit/attendance',
  component: ScheduleEditAttendanceRoute
});

const scheduleDeleteRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'schedules/$scheduleId/delete',
  component: ScheduleDeleteRoute
});

const assignmentDetailRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'assignments/$assignmentId',
  component: AssignmentDetailRoute
});

const assignmentCreateRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'assignments/new',
  component: AssignmentCreateRoute
});

const assignmentUploadRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'assignments/$assignmentId/submission',
  component: AssignmentSubmissionRoute
});

const choirManagementRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'manage/choir',
  component: ChoirManagementRoute
});

const scheduleSettingsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'settings/schedules',
  component: ScheduleSettingsRoute
});

const memberSettingsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'settings/members',
  component: MemberSettingsRoute
});

const seasonSettingsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'settings/season',
  component: () => <RoutePlaceholder title="乐季设置" />
});

const approvalListRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'manage/approvals',
  component: ApprovalListRoute
});

const approvalDetailRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'manage/approvals/$joinRequestId',
  component: ApprovalDetailRoute
});

const invitationCodesRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'manage/invitations',
  component: InvitationCodesRoute
});

const memberGroupsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'member-groups',
  component: MemberGroupsRoute
});

const createMemberGroupRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'member-groups/new',
  component: CreateMemberGroupRoute
});

const createMemberGroupInitialMemberRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'member-groups/new/initial-member',
  component: CreateMemberGroupInitialMemberRoute
});

const permissionGroupsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'permission-groups',
  component: PermissionGroupsRoute
});

const createPermissionGroupRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'permission-groups/new',
  component: CreatePermissionGroupRoute
});

const permissionGroupDetailRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'permission-groups/$permissionGroupId',
  component: PermissionGroupDetailRoute
});

const editPermissionGroupRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'permission-groups/$permissionGroupId/edit',
  component: EditPermissionGroupRoute
});

const deletePermissionGroupRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'permission-groups/$permissionGroupId/delete',
  component: DeletePermissionGroupRoute
});

const managePermissionGroupMembersRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'permission-groups/$permissionGroupId/members',
  component: ManagePermissionGroupMembersRoute
});

const removePermissionGroupMemberRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'permission-groups/$permissionGroupId/members/$seasonMembershipId/remove',
  component: RemovePermissionGroupMemberRoute
});

const memberGroupDetailRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'member-groups/$memberGroupId',
  validateSearch: validateMemberGroupDetailSearch,
  component: MemberGroupDetailRoute
});

const renameMemberGroupRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'member-groups/$memberGroupId/rename',
  component: RenameMemberGroupRoute
});

const deleteMemberGroupRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'member-groups/$memberGroupId/delete',
  component: DeleteMemberGroupRoute
});

const manageMemberGroupMembersRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'member-groups/$memberGroupId/members',
  component: ManageMemberGroupMembersRoute
});

const removeMemberGroupMemberRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'member-groups/$memberGroupId/members/$seasonMembershipId/remove',
  component: RemoveMemberGroupMemberRoute
});

const manageMemberGroupTagsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'member-groups/$memberGroupId/tags',
  component: ManageMemberGroupTagsRoute
});

const createMemberGroupTagRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'member-groups/$memberGroupId/tags/new',
  component: CreateMemberGroupTagRoute
});

const memberGroupTagDetailRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'member-groups/$memberGroupId/tags/$memberGroupTagId',
  component: MemberGroupTagDetailRoute
});

const renameMemberGroupTagRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'member-groups/$memberGroupId/tags/$memberGroupTagId/rename',
  component: RenameMemberGroupTagRoute
});

const deleteMemberGroupTagRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'member-groups/$memberGroupId/tags/$memberGroupTagId/delete',
  component: DeleteMemberGroupTagRoute
});

const createAttendanceCategoryRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'settings/attendance-categories/new',
  component: CreateAttendanceCategoryRoute
});

const renameAttendanceCategoryRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'settings/attendance-categories/$attendanceCategoryId/rename',
  component: RenameAttendanceCategoryRoute
});

const disableAttendanceCategoryRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'settings/attendance-categories/$attendanceCategoryId/disable',
  component: DisableAttendanceCategoryRoute
});

export const routeTree = rootRoute.addChildren([
  indexRoute,
  authRoute,
  onboardingRoute,
  workspaceRoute.addChildren([
    workspaceTabRoute,
    notificationsRoute,
    songDetailRoute,
    songRelatedFolderRoute,
    resourceFilePreviewRoute,
    scheduleDetailRoute,
    scheduleCreateRoute,
    scheduleEditRoute,
    scheduleCreateTimeRoute,
    scheduleEditTimeRoute,
    scheduleCreateAttendanceRoute,
    scheduleEditAttendanceRoute,
    scheduleDeleteRoute,
    assignmentCreateRoute,
    assignmentDetailRoute,
    assignmentUploadRoute,
    choirManagementRoute,
    scheduleSettingsRoute,
    memberSettingsRoute,
    seasonSettingsRoute,
    approvalListRoute,
    approvalDetailRoute,
    invitationCodesRoute,
    memberGroupsRoute,
    createMemberGroupRoute,
    createMemberGroupInitialMemberRoute,
    permissionGroupsRoute,
    createPermissionGroupRoute,
    permissionGroupDetailRoute,
    editPermissionGroupRoute,
    deletePermissionGroupRoute,
    managePermissionGroupMembersRoute,
    removePermissionGroupMemberRoute,
    memberGroupDetailRoute,
    renameMemberGroupRoute,
    deleteMemberGroupRoute,
    manageMemberGroupMembersRoute,
    removeMemberGroupMemberRoute,
    manageMemberGroupTagsRoute,
    createMemberGroupTagRoute,
    memberGroupTagDetailRoute,
    renameMemberGroupTagRoute,
    deleteMemberGroupTagRoute,
    createAttendanceCategoryRoute,
    renameAttendanceCategoryRoute,
    disableAttendanceCategoryRoute
  ])
]);

function AppShellRoute(): ReactNode {
  return <AppShell authRoute={<AuthRoute />} onboardingRoute={<OnboardingRoute />} />;
}

function WorkspaceRouteOutlet(): ReactNode {
  return <Outlet />;
}

function WorkspaceTabRoute(): ReactNode {
  const routeAdapter = useWorkspaceRouteAdapter();
  const { tab } = workspaceTabRoute.useParams();
  const workspaceTab = isWorkspaceTab(tab) ? tab : null;
  useEffect(() => {
    if (!workspaceTab) {
      routeAdapter.replaceMainTab('home');
    }
  }, [routeAdapter, workspaceTab]);

  return workspaceTab ? <WorkspaceTabContent tab={workspaceTab} /> : null;
}

function WorkspaceTabContent({ tab }: { readonly tab: WorkspaceTab }): ReactNode {
  const appContext = useWorkspaceBootstrapContext();

  return (
    <div className="route-outlet" data-workspace-context="">
      {renderWorkspaceTabContent(tab, appContext)}
    </div>
  );
}

function renderWorkspaceTabContent(tab: WorkspaceTab, appContext: WorkspaceBootstrapContext): ReactNode {
  switch (tab) {
    case 'home':
      return (
        <WorkspaceHomeTab
          appContext={appContext}
          renderScheduleRows={(schedules) => <ScheduleRows schedules={schedules} />}
        />
      );
    case 'assignments':
      return <WorkspaceAssignmentsTab />;
    case 'schedule':
      return <WorkspaceScheduleTabRoute />;
    case 'resources':
      return <WorkspaceResourcesTab />;
    case 'members':
      return <WorkspaceMembersTab />;
  }
}

function AssignmentCreateRoute(): ReactNode {
  return <AssignmentCreatePage />;
}

function WorkspaceScheduleTabRoute(): ReactNode {
  const search = workspaceTabRoute.useSearch();
  return <WorkspaceScheduleTab search={search} />;
}

function NotificationsRoute(): ReactNode {
  return <NotificationsPage />;
}

function SongDetailRoute(): ReactNode {
  const { songId } = songDetailRoute.useParams();
  return <SongDetailPage songId={songId} />;
}

function SongRelatedFolderRoute(): ReactNode {
  const { songId, folderId } = songRelatedFolderRoute.useParams();
  return <SongRelatedFolderPage songId={songId} folderId={folderId} />;
}

function ResourceFilePreviewRoute(): ReactNode {
  const { resourceFileId } = resourceFilePreviewRoute.useParams();
  return <ResourceFilePreviewPage resourceFileId={resourceFileId} />;
}

function IndexRoute(): ReactNode {
  return <WorkspaceTabContent tab="home" />;
}

function AuthRoute(): ReactNode {
  return <AuthPage />;
}

function OnboardingRoute(): ReactNode {
  return <OnboardingPage />;
}

function ScheduleDetailRoute(): ReactNode {
  const { scheduleId } = scheduleDetailRoute.useParams();
  return <ScheduleDetailPage scheduleId={scheduleId} />;
}

function ScheduleCreateRoute(): ReactNode {
  const search = scheduleCreateRoute.useSearch();
  return <ScheduleCreatePage selectedDate={search.date} />;
}

function ScheduleEditRoute(): ReactNode {
  const { scheduleId } = scheduleEditRoute.useParams();
  return <ScheduleEditPage scheduleId={scheduleId} />;
}

function ScheduleCreateTimeRoute(): ReactNode {
  return <ScheduleCreateTimePage />;
}

function ScheduleEditTimeRoute(): ReactNode {
  const { scheduleId } = scheduleEditTimeRoute.useParams();
  return <ScheduleEditTimePage scheduleId={scheduleId} />;
}

function ScheduleCreateAttendanceRoute(): ReactNode {
  return <ScheduleCreateAttendancePage />;
}

function ScheduleEditAttendanceRoute(): ReactNode {
  const { scheduleId } = scheduleEditAttendanceRoute.useParams();
  return <ScheduleEditAttendancePage scheduleId={scheduleId} />;
}

function ScheduleDeleteRoute(): ReactNode {
  const { scheduleId } = scheduleDeleteRoute.useParams();
  return <ScheduleDeletePage scheduleId={scheduleId} />;
}

function AssignmentDetailRoute(): ReactNode {
  const { assignmentId } = assignmentDetailRoute.useParams();
  return <AssignmentDetailPage assignmentId={assignmentId} />;
}

function AssignmentSubmissionRoute(): ReactNode {
  const { assignmentId } = assignmentUploadRoute.useParams();
  return <AssignmentSubmissionPage assignmentId={assignmentId} />;
}

function MemberSettingsRoute(): ReactNode {
  return <MemberSettingsPage />;
}

function ScheduleSettingsRoute(): ReactNode {
  return <ScheduleSettingsPage />;
}

function CreateAttendanceCategoryRoute(): ReactNode {
  return <CreateAttendanceCategoryPage />;
}

function RenameAttendanceCategoryRoute(): ReactNode {
  const { attendanceCategoryId } = renameAttendanceCategoryRoute.useParams();
  return <RenameAttendanceCategoryPage attendanceCategoryId={attendanceCategoryId} />;
}

function DisableAttendanceCategoryRoute(): ReactNode {
  const { attendanceCategoryId } = disableAttendanceCategoryRoute.useParams();
  return <DisableAttendanceCategoryPage attendanceCategoryId={attendanceCategoryId} />;
}

function CreateMemberGroupRoute(): ReactNode {
  return <CreateMemberGroupPage />;
}

function CreateMemberGroupInitialMemberRoute(): ReactNode {
  return <CreateMemberGroupInitialMemberPage />;
}

function MemberGroupsRoute(): ReactNode {
  return <MemberGroupsPage />;
}

function PermissionGroupsRoute(): ReactNode {
  return <PermissionGroupsPage />;
}

function CreatePermissionGroupRoute(): ReactNode {
  return <CreatePermissionGroupPage />;
}

function PermissionGroupDetailRoute(): ReactNode {
  const { permissionGroupId } = permissionGroupDetailRoute.useParams();
  return <PermissionGroupDetailPage permissionGroupId={permissionGroupId} />;
}

function EditPermissionGroupRoute(): ReactNode {
  const { permissionGroupId } = editPermissionGroupRoute.useParams();
  return <EditPermissionGroupPage permissionGroupId={permissionGroupId} />;
}

function DeletePermissionGroupRoute(): ReactNode {
  const { permissionGroupId } = deletePermissionGroupRoute.useParams();
  return <DeletePermissionGroupPage permissionGroupId={permissionGroupId} />;
}

function ManagePermissionGroupMembersRoute(): ReactNode {
  const { permissionGroupId } = managePermissionGroupMembersRoute.useParams();
  return <ManagePermissionGroupMembersPage permissionGroupId={permissionGroupId} />;
}

function RemovePermissionGroupMemberRoute(): ReactNode {
  const { permissionGroupId, seasonMembershipId } = removePermissionGroupMemberRoute.useParams();
  return (
    <RemovePermissionGroupMemberPage permissionGroupId={permissionGroupId} seasonMembershipId={seasonMembershipId} />
  );
}

function MemberGroupDetailRoute(): ReactNode {
  const { memberGroupId } = memberGroupDetailRoute.useParams();
  const search = memberGroupDetailRoute.useSearch();
  return <MemberGroupDetailPage memberGroupId={memberGroupId} tab={search.tab ?? 'members'} />;
}

function RenameMemberGroupRoute(): ReactNode {
  const { memberGroupId } = renameMemberGroupRoute.useParams();
  return <RenameMemberGroupPage memberGroupId={memberGroupId} />;
}

function DeleteMemberGroupRoute(): ReactNode {
  const { memberGroupId } = deleteMemberGroupRoute.useParams();
  return <DeleteMemberGroupPage memberGroupId={memberGroupId} />;
}

function ManageMemberGroupMembersRoute(): ReactNode {
  const { memberGroupId } = manageMemberGroupMembersRoute.useParams();
  return <ManageMemberGroupMembersPage memberGroupId={memberGroupId} />;
}

function RemoveMemberGroupMemberRoute(): ReactNode {
  const { memberGroupId, seasonMembershipId } = removeMemberGroupMemberRoute.useParams();
  return <RemoveMemberGroupMemberPage memberGroupId={memberGroupId} seasonMembershipId={seasonMembershipId} />;
}

function ManageMemberGroupTagsRoute(): ReactNode {
  const { memberGroupId } = manageMemberGroupTagsRoute.useParams();
  return <ManageMemberGroupTagsPage memberGroupId={memberGroupId} />;
}

function CreateMemberGroupTagRoute(): ReactNode {
  const { memberGroupId } = createMemberGroupTagRoute.useParams();
  return <CreateMemberGroupTagPage memberGroupId={memberGroupId} />;
}

function MemberGroupTagDetailRoute(): ReactNode {
  const { memberGroupId, memberGroupTagId } = memberGroupTagDetailRoute.useParams();
  return <MemberGroupTagDetailPage memberGroupId={memberGroupId} memberGroupTagId={memberGroupTagId} />;
}

function RenameMemberGroupTagRoute(): ReactNode {
  const { memberGroupId, memberGroupTagId } = renameMemberGroupTagRoute.useParams();
  return <RenameMemberGroupTagPage memberGroupId={memberGroupId} memberGroupTagId={memberGroupTagId} />;
}

function DeleteMemberGroupTagRoute(): ReactNode {
  const { memberGroupId, memberGroupTagId } = deleteMemberGroupTagRoute.useParams();
  return <DeleteMemberGroupTagPage memberGroupId={memberGroupId} memberGroupTagId={memberGroupTagId} />;
}

function ChoirManagementRoute(): ReactNode {
  return <ChoirManagementPage />;
}

function ApprovalListRoute(): ReactNode {
  return <ApprovalListPage />;
}

function ApprovalDetailRoute(): ReactNode {
  const { joinRequestId } = approvalDetailRoute.useParams();
  return <ApprovalDetailPage joinRequestId={joinRequestId} />;
}

function InvitationCodesRoute(): ReactNode {
  return <InvitationCodesPage />;
}

function validateWorkspaceTabSearch(search: Record<string, unknown>): WorkspaceTabSearch {
  return {
    mode: isScheduleMode(search.mode) ? search.mode : undefined,
    date: isDateKey(search.date) ? search.date : undefined,
    month: isMonthKey(search.month) ? search.month : undefined
  };
}

function validateScheduleCreateSearch(search: Record<string, unknown>): ScheduleCreateSearch {
  return {
    date: isDateKey(search.date) ? search.date : undefined
  };
}

function validateMemberGroupDetailSearch(search: Record<string, unknown>): MemberGroupDetailSearch {
  return {
    tab: isMemberGroupDetailTab(search.tab) ? search.tab : undefined
  };
}

function isScheduleMode(value: unknown): value is ScheduleMode {
  return value === 'list' || value === 'calendar';
}

function isDateKey(value: unknown): value is string {
  if (typeof value !== 'string' || !DATE_KEY_PATTERN.test(value)) {
    return false;
  }

  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() + 1 === month &&
    date.getUTCDate() === day &&
    formatDateKey(date) === value
  );
}

function isMonthKey(value: unknown): value is string {
  if (typeof value !== 'string' || !MONTH_KEY_PATTERN.test(value)) {
    return false;
  }

  const [year, month] = value.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, 1));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() + 1 === month &&
    `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}` === value
  );
}
